import { call, put, select, takeEvery } from 'redux-saga/effects';
import { AnyAction } from 'redux';
import {
  getSelectedZusatzoptionen,
  GlobalActions,
  GlobalPut,
  TkvV2CheckoutProduktPayload,
} from '@ps20-frontend/shop-integration-redux';
import { App, Produktlinie } from '@ps20-frontend/frontend-models';
import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../appState';
import { TkvActions } from '../tkvActions';
import {
  convertAnimalTypeToTierart,
  getBreedValueOrLabel,
  getBreedObject
} from './transformer/tkvData.transformer';

export function* watchContinueToDashboardSaga() {
  yield takeEvery(TkvActions.CONTINUE_TO_DASHBOARD, safeSaga(generateDashboardData, App.TKV_V2));
  yield takeEvery(TkvActions.CONTINUE_TO_DASHBOARD, safeSaga(generateProduktData, App.TKV_V2));
}

export function* generateProduktData() {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();

  const breedLabel = getBreedValueOrLabel(state.versicherungsnehmer, 'label');

  const produktData: TkvV2CheckoutProduktPayload = {
    appId: App.TKV_V2,
    angebotsId: state.angebotsId,
    laufzeit: state.vertragsoptionen.vertragslaufzeit,
    uebersichtTitle: 'Tierkrankenversicherung',
    versichertesRisiko: `${breedLabel}, geb. ${state.versicherungsnehmer.animalBirthDate}`,
    vorvertragsnummer: state.vertragsoptionen.vorvertragsNummern,
    vorvertragSofortschutz: state.vertragsoptionen.vorvertragSofortschutz,
    beibehalten: state.vertragsoptionen.beibehalten,
    beginndatum: state.vertragsoptionen.beginnDatum,
    angebotsdatum: state.vertragsoptionen.angebotsdatum,
    animalType: convertAnimalTypeToTierart(state.versicherungsnehmer.animalType),
    animalName: state.versicherungsnehmer.animalName,
    animalBirthDate: state.versicherungsnehmer.animalBirthDate,
    animalGender: state.versicherungsnehmer.animalGender,
    breed: getBreedObject(state.versicherungsnehmer),
    catKeepingType: state.versicherungsnehmer.catKeepingType,
    hasIllnesses: state.versicherungsnehmer.hasIllnesses,
    selectedIllnesses: state.versicherungsnehmer.selectedIllnesses,
    previousDiagnoses: state.versicherungsnehmer.previousDiagnoses,
    animalIdentificationType: state.versicherungsnehmer.kennzeichnungsArt,
    animalIdentification: state.versicherungsnehmer.kennzeichen,
    isAssistanceDog: state.versicherungsnehmer.isAssistanceDog,
    isVnWechsel: state.vorbelegungen?.isVnWechsel,
  };
  yield call([globalPut, globalPut.put], new GlobalActions().produktAction(produktData));
}

export function* generateDashboardData(action?: AnyAction) {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const selectedProduktlinie: Produktlinie = getSelectedProduktlinie(state.produkt);

  yield put(new TkvActions().vorschlagdatenChanged(action?.payload?.deferTarifierung === true));

  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().inDashboardUebernehmen({
      appId: App.TKV_V2,
      angebotsId: state.angebotsId,
      kurztext: selectedProduktlinie.kurztext,
      ausgewaehlteLinie: selectedProduktlinie.produktName,
      versichertesRisiko: getVersichertesRisiko(state),
      tierSubline: getTierSubline(state),
      vertragslaufzeit: state.vertragsoptionen.vertragslaufzeit,
      beginn: state.vertragsoptionen.beginnDatum,
      selectZusatzoptionen: getSelectedZusatzoptionen(selectedProduktlinie.zusatzoptionen),
      tierart: state.versicherungsnehmer.animalType,
      coverageType: state.vertragsoptionen.coverageType,
      ...(action?.payload?.deferTarifierung ? { deferTarifierung: true } : {}),
    }),
  );
}

const getTierSubline = (state: IAppState): string =>
  [
    state.versicherungsnehmer.animalName,
    getBreedValueOrLabel(state.versicherungsnehmer, 'label'),
    state.versicherungsnehmer.animalBirthDate,
  ]
    .filter(Boolean)
    .join(', ');

export const getVersichertesRisiko = (state: IAppState) => {
  const breedLabel = getBreedValueOrLabel(state.versicherungsnehmer, 'label');
  return `${breedLabel}, ${state.versicherungsnehmer.animalBirthDate}`;
};
